import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import TrialBanner from "./TrialBanner";
import ImpersonationBanner from "./ImpersonationBanner";
import TenantDeactivationBanner from "./TenantDeactivationBanner";
import { useAuth } from "@/lib/auth-context";
import OnboardingReturnBanner from "../onboarding/OnboardingReturnBanner";
import Breadcrumbs from "./Breadcrumbs";
import SenseAIBuilderFAB from "../SenseAIBuilderFAB";
import UpsellToast from "../billing/UpsellToast";
import AiCreditsUpsell from "../billing/AiCreditsUpsell";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Outlet } from "react-router-dom";
import { BreadcrumbLabelProvider } from "@/lib/breadcrumb-labels";

/**
 * iter-121.78 — Responsive shell.
 *
 *   Desktop (≥768px / md+): persistent sidebar on the left, collapsible
 *     between 240px and 64px just like before.
 *   Mobile (<768px): sidebar lives inside a Sheet drawer slid in from
 *     the left, opened by a hamburger in the TopBar. Tapping a nav
 *     link inside the drawer closes it.
 *
 *   The same `Sidebar` component is used in both modes — no
 *   duplication — so the nav list, branding, and active-state logic
 *   stay in one place.
 */
export default function AppShell() {
    const { user } = useAuth();
    // Persist the sidebar collapsed/expanded choice PER USER across sessions.
    // First ever session defaults to collapsed; afterwards we remember the
    // user's last choice (keyed by user id so shared browsers stay separate).
    const storageKey = `analyzio_sidebar_collapsed_${user?.id || "anon"}`;
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const impersonating = !!user?.impersonated_by;

    // iter-2026 — Sidebar ALWAYS loads expanded (requirement). On every mount
    // (login, refresh, first entry into the shell) we start expanded so the main
    // nav sections are visible. The user can still collapse it manually via the
    // Collapse control during the session; that transient choice is intentionally
    // NOT restored on the next load, so the default stays consistent everywhere.
    useEffect(() => {
        setCollapsed(false);
    }, [storageKey]);

    const toggleSidebar = () => {
        setCollapsed((c) => {
            const next = !c;
            try { localStorage.setItem(storageKey, String(next)); } catch { /* ignore */ }
            return next;
        });
    };

    // Auto-close the mobile drawer whenever the user navigates so
    // taps on nav links inside the sheet land on the new page with
    // the sheet dismissed (the more common UX expectation than the
    // user manually closing the sheet first).
    React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    return (
        <BreadcrumbLabelProvider>
        <div className="flex h-screen overflow-hidden bg-background text-foreground">
            {/* Desktop sidebar — hidden below md breakpoint. */}
            <div className="hidden md:flex">
                <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />
            </div>

            {/* Mobile sidebar — Sheet drawer. */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetContent
                    side="left"
                    className="p-0 w-[260px] sm:w-[280px] border-r border-border md:hidden"
                    data-testid="mobile-sidebar-sheet"
                >
                    {/* Force `collapsed=false` so the mobile drawer always
                        shows full labels. No collapse toggle inside the
                        drawer — that affordance only exists on desktop. */}
                    <Sidebar
                        collapsed={false}
                        onToggle={null}
                        mobileMode
                        onLinkClick={() => setMobileOpen(false)}
                    />
                </SheetContent>
            </Sheet>

            <div className="flex-1 flex flex-col min-w-0">
                <ImpersonationBanner />
                <TenantDeactivationBanner />
                <OnboardingReturnBanner />
                <TopBar onMobileMenuClick={() => setMobileOpen(true)} impersonating={impersonating} />
                <TrialBanner />
                <main className="flex-1 min-h-0 overflow-auto" data-testid="app-main">
                    <Breadcrumbs />
                    <Outlet />
                </main>
            </div>
            <SenseAIBuilderFAB />
            {/* iter-120 B4 — global 402 plan-limit toast listener */}
            <UpsellToast />
            <AiCreditsUpsell />
        </div>
        </BreadcrumbLabelProvider>
    );
}
